import { useCallback, useRef } from 'react';

// A computed length can come back as '' for properties the environment does not
// resolve; NaN would reach `style.height` and be dropped without a trace.
const toPx = (value: string) => parseFloat(value) || 0;

/**
 * Writes an explicit pixel height on the modal's body so CSS can transition it:
 * `height: auto` cannot be interpolated, and the dialog is centered, so an
 * unmounted field otherwise moves both edges in one frame (CEXP-22827).
 *
 * The dialog itself stays `auto`, so header and footer changes are folded in by
 * layout with nothing to observe.
 */
export const useAnimatedHeight = () => {
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const lastHeightRef = useRef<number | null>(null);

  // The entry's box ignores the dialog's `contentShow` scale, which is still
  // running when the first entry arrives.
  const sync = useCallback((entry: ResizeObserverEntry) => {
    const body = bodyRef.current;

    if (!body) {
      return;
    }

    const bodyStyle = getComputedStyle(body);
    const target =
      entry.contentRect.height +
      toPx(bodyStyle.paddingTop) +
      toPx(bodyStyle.paddingBottom) +
      toPx(bodyStyle.borderTopWidth) +
      toPx(bodyStyle.borderBottomWidth);

    // Sub-pixel churn would otherwise restart the transition on every observation.
    if (
      lastHeightRef.current !== null &&
      Math.abs(target - lastHeightRef.current) < 1
    ) {
      return;
    }

    lastHeightRef.current = target;
    // The body keeps `flex-shrink` and `overflow-y-auto`, so a target past the
    // dialog's `max-h-[85vh]` scrolls instead of overflowing it.
    body.style.height = `${target}px`;
  }, []);

  // A callback ref rather than an effect: Radix mounts the dialog's subtree in a
  // later commit than the one this component's effects run in, so an effect finds
  // every ref still null and — its deps unchanged — never looks again.
  const setMeasureRef = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      lastHeightRef.current = null;

      if (!node) {
        bodyRef.current?.style.removeProperty('height');
        return;
      }

      // Observing delivers an entry before the next paint. That first write equals
      // the height the body already has — and a transition from `auto` cannot run
      // — so opening never animates.
      const observer = new ResizeObserver(([entry]) => {
        if (entry) {
          sync(entry);
        }
      });
      observer.observe(node);
      observerRef.current = observer;
    },
    [sync],
  );

  return { bodyRef, setMeasureRef };
};
